import {EditEventSection} from '@/components/adminEditEvent/adminEditEvent';
import {CloneEventDialog} from '@/components/cloneEventDialog/cloneEventDialog';
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {Button} from '@/components/ui/button';
import {Spinner} from '@/components/ui/spinner';
import {toastManager} from '@/components/ui/toast';
import {Typography} from '@/components/ui/typography';
import {archiveEvent, restoreEvent} from '@/server/event';
import {createFileRoute, getRouteApi, useRouter} from '@tanstack/react-router';
import {Archive, ArchiveRestore} from 'lucide-react';
import {useState} from 'react';

const dashboardRoute = getRouteApi('/admin/dashboard/event/$eventId');

export const Route = createFileRoute('/admin/dashboard/event/$eventId/edit')({
  component: EditPage,
});

/** Dashboard edit section rendered as its own route for deep linking. */
function EditPage() {
  const {event} = dashboardRoute.useLoaderData();
  const router = useRouter();
  const [isArchiving, setIsArchiving] = useState(false);

  /** Executes the archive or restore action. */
  const handleArchiveAction = async () => {
    setIsArchiving(true);
    try {
      if (event.archived) {
        await restoreEvent({data: event.id});
        toastManager.add({title: 'Event restored', type: 'success'});
      } else {
        await archiveEvent({data: event.id});
        toastManager.add({title: 'Event archived', type: 'success'});
      }
      router.invalidate();
    } catch (err) {
      toastManager.add({
        title: err instanceof Error ? err.message : 'Something went wrong',
        type: 'error',
      });
    } finally {
      setIsArchiving(false);
    }
  };

  return (
    <div className="space-y-8">
      <EditEventSection event={event} />

      <div className="border-t border-neutral-200 pt-6">
        <Typography level="B4" className="text-neutral-900 mb-2">
          Clone Event
        </Typography>
        <Typography level="B3" className="text-neutral-500 mb-4">
          Creates a copy of this event with a new start date.
        </Typography>
        <CloneEventDialog event={{id: event.id, startDate: event.startDate}} />
      </div>

      {!event.externalId && (
        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-sm font-medium text-gray-900 mb-2">
            {event.archived ? 'Restore Event' : 'Archive Event'}
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            {event.archived
              ? 'This event is archived and hidden from public search. Restore it to make it visible again.'
              : 'Archiving hides this event from public search and the map. You can restore it later.'}
          </p>
          <AlertDialog>
            <AlertDialogTrigger
              render={
                <Button variant="outline" disabled={isArchiving}>
                  {isArchiving ? (
                    <Spinner size="sm" />
                  ) : event.archived ? (
                    <>
                      <ArchiveRestore className="w-4 h-4 mr-2" />
                      Restore Event
                    </>
                  ) : (
                    <>
                      <Archive className="w-4 h-4 mr-2" />
                      Archive Event
                    </>
                  )}
                </Button>
              }
            />
            <AlertDialogPopup>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {event.archived
                    ? 'Restore this event?'
                    : 'Archive this event?'}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {event.archived
                    ? 'This event will be visible again in public search and the map.'
                    : 'This event will be hidden from public search and the map. You can restore it later from the organizer dashboard.'}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogClose
                  render={<Button variant="outline">Cancel</Button>}
                />
                <AlertDialogClose
                  render={
                    <Button onClick={handleArchiveAction}>
                      {event.archived ? 'Restore Event' : 'Archive Event'}
                    </Button>
                  }
                />
              </AlertDialogFooter>
            </AlertDialogPopup>
          </AlertDialog>
        </div>
      )}
    </div>
  );
}
